<script setup lang="ts">
import { AccountIcon, EmptyState } from '@taler/ui'
import { formatRatioPercent, type Locale, type Ratio } from '@taler/domain'
import MoneyAmount from '@/app/components/MoneyAmount.vue'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { RouteLocationRaw } from 'vue-router'
import type { TopListRow } from '../internal/view-types.ts'

defineProps<{ title: string; rows: readonly TopListRow[]; viewAll: RouteLocationRaw }>()
const { t, locale } = useI18n()
const currentLocale = computed(() => locale.value as Locale)

function percentText(share: Ratio | undefined): string {
  if (share === undefined) return '—'
  return `${formatRatioPercent(share, { locale: currentLocale.value, minimumFractionDigits: 0, maximumFractionDigits: 0 })}%`
}
</script>

<template>
  <section
    class="flex min-w-0 flex-col gap-[var(--space-inline)] rounded-[var(--radius-surface)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)] p-[var(--space-group)]"
  >
    <header class="flex items-center justify-between gap-2">
      <h2 class="text-[length:var(--text-step-3)] font-semibold text-[color:var(--color-text)]">
        {{ title }}
      </h2>
      <RouterLink :to="viewAll" class="text-[length:var(--text-step-1)] text-[color:var(--color-accent)]">
        {{ t('reporting.viewAll') }}
      </RouterLink>
    </header>
    <EmptyState v-if="rows.length === 0" :title="t('reporting.empty.title')" />
    <ol v-else class="flex flex-col">
      <li
        v-for="(row, index) in rows"
        :key="row.key"
        class="grid grid-cols-[1.5rem_minmax(0,1fr)_auto_3.5rem] items-center gap-[var(--space-inline)] border-b border-[color:var(--color-border)] py-[var(--space-inline)] text-[length:var(--text-step-2)] last:border-b-0"
      >
        <span class="tabular-nums text-[color:var(--color-text-muted)]">{{ index + 1 }}</span>
        <span class="flex min-w-0 items-center gap-2">
          <span aria-hidden="true" class="shrink-0"
            ><AccountIcon v-if="row.icon" :icon="row.icon" label=""
          /></span>
          <span class="truncate text-[color:var(--color-text)]">{{ row.label }}</span>
        </span>
        <MoneyAmount :value="row.amount" size="figure" />
        <span class="text-right tabular-nums text-[color:var(--color-text-muted)]">{{
          percentText(row.share)
        }}</span>
      </li>
    </ol>
  </section>
</template>
